/**
 * Zeitpunkte in der Uhr des Ortes — nicht in der des Geräts.
 *
 * **Der Grund steht in der Sache, nicht in der Technik.** In der Datenbank
 * steht durchgehend UTC; die Oberfläche rechnete das bisher in die Uhr um, auf
 * die der Browser gerade gestellt ist. Bei einer Reise durch andere Zeitzonen
 * ist das die falsche Zahl: wer den Bericht abends zu Hause durchsieht, las
 * jede Ankunft um Stunden verschoben. Die Uhrzeit war richtig und meinte
 * trotzdem den falschen Augenblick.
 *
 * Gilt ein Versatz, wird er hier angewandt — und **dazugeschrieben, welche Uhr
 * gemeint ist**. Eine Uhrzeit ohne Uhr ist eine halbe Angabe, und das gilt in
 * beide Richtungen: auch „Gerätezeit" will benannt sein.
 *
 * `null` heißt: keine Angabe, es bleibt bei der Uhr des Geräts. Genau so ist
 * es überall bisher gewesen, und genau so bleibt es für jede Reise, an der
 * niemand etwas einstellt.
 *
 * **Das Sprachgebiet steht in `texte/de.ts`** (`allgemein.gebiet`): mit der
 * Sprache wechselt auch das Format, und die Reihenfolge von Tag und Monat ist
 * keine Kleinigkeit.
 */
import { WORTLAUT } from '../texte'

const GEBIET = WORTLAUT.allgemein.gebiet

/** Wie viele Minuten der Ort von UTC entfernt ist — oder `null` fürs Gerät. */
export type Versatz = number | null | undefined

const MINUTE = 60_000

/**
 * Der Zeitpunkt, verschoben in die Uhr des Ortes.
 *
 * Der Kniff: statt eine Zeitzone zu setzen (die der Browser kennen müsste),
 * wird der Augenblick um den Versatz verschoben und danach in UTC gelesen.
 * Das Ergebnis ist dieselbe Zahl, die eine Uhr am Ort zeigen würde — auf
 * jedem Gerät gleich, egal wie es steht.
 */
function verschoben(iso: string, versatz: Versatz): Date | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  if (versatz === null || versatz === undefined) return d
  return new Date(d.getTime() + versatz * MINUTE)
}

/** Ob mit `verschoben()` gerechnet wurde — dann liest man das Ergebnis in UTC. */
function zone(versatz: Versatz): { timeZone?: string } {
  return versatz === null || versatz === undefined ? {} : { timeZone: 'UTC' }
}

/** „1. Aug. 2026, 14:30" — Datum und Uhrzeit. */
export function zeitText(iso: string, versatz: Versatz = null): string {
  const d = verschoben(iso, versatz)
  if (d === null) return ''
  return d.toLocaleString(GEBIET, {
    day: 'numeric',
    month: 'short',
    // **Mit Jahr.** Ein Reisebericht wird Jahre später gelesen, und „5. Okt."
    // ist dann die halbe Angabe. In der Bearbeitungsmaske stand es längst --
    // aus dem Betrieb gemeldet, dass es in der Anzeige fehlt.
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...zone(versatz),
  })
}

/**
 * „1. Aug. 2026" — nur der Tag.
 *
 * Für die Deckblätter der Highlight-Karten: dort ist Platz für Titel, Ort und
 * Datum, und die Uhrzeit einer Ankunft sagt beim Überfliegen nichts. Sie steht
 * im Blatt dahinter, wo sie hingehört. Aus dem Betrieb gewünscht.
 */
export function datumText(iso: string, versatz: Versatz = null): string {
  const d = verschoben(iso, versatz)
  if (d === null) return ''
  return d.toLocaleDateString(GEBIET, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...zone(versatz),
  })
}

/**
 * „Montag, 5. Oktober 2026" — der Tagestrenner im Buch.
 *
 * **Und ausdrücklich nicht `dayLabel()` aus `lib/format.ts`.** Die ist für die
 * Chatliste gebaut: sie sagt „Heute", „Gestern", „Montag" und rechnet die
 * Tagesgrenze in der Uhr des Geräts. In einem gedruckten Buch ist „Heute" ein
 * Jahr später keine Angabe mehr, und wer den Bericht aus Norwegen zu Hause in
 * Kalifornien druckt, bekommt die Beiträge unter den falschen Tag. Auf Papier
 * steht deshalb der volle Tag, in der Uhr der Reise.
 *
 * Der Wochentag steht dabei, weil ein Reisebericht davon lebt: „Sonntag" sagt
 * mehr über den Tag als „11.".
 */
export function buchTag(iso: string, versatz: Versatz = null): string {
  const d = verschoben(iso, versatz)
  if (d === null) return ''
  return d.toLocaleDateString(GEBIET, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...zone(versatz),
  })
}

/**
 * „14:30" — die Uhrzeit allein.
 *
 * Im Buch steht der Tag schon im Trenner darüber; ihn an jeder Nachricht zu
 * wiederholen macht aus einer Zeitangabe eine Zeile Rauschen. Auf dem
 * Bildschirm gibt es dafür `bubbleTime()`, die aber in der Uhr des Geräts
 * rechnet — hier gilt die der Reise.
 */
export function uhrText(iso: string, versatz: Versatz = null): string {
  const d = verschoben(iso, versatz)
  if (d === null) return ''
  return d.toLocaleTimeString(GEBIET, {
    hour: '2-digit',
    minute: '2-digit',
    ...zone(versatz),
  })
}

/**
 * ISO-Zeit in das Format von `<input type="datetime-local">`.
 *
 * Das Feld kennt keine Zeitzone; es zeigt schlicht die Zahlen, die es bekommt.
 * Welche Uhr gemeint ist, muss deshalb danebenstehen — `uhrName()`.
 */
export function zuFeld(iso: string | null, versatz: Versatz = null): string {
  if (iso === null) return ''
  const d = verschoben(iso, versatz)
  if (d === null) return ''

  const zwei = (n: number) => String(n).padStart(2, '0')
  const [jahr, monat, tag, stunde, minute] =
    versatz === null || versatz === undefined
      ? [d.getFullYear(), d.getMonth() + 1, d.getDate(), d.getHours(), d.getMinutes()]
      : [d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes()]

  return `${jahr}-${zwei(monat)}-${zwei(tag)}T${zwei(stunde)}:${zwei(minute)}`
}

/** Und zurück: was im Feld steht, als UTC-Zeitpunkt für den Server. */
export function ausFeld(wert: string, versatz: Versatz = null): string | undefined {
  if (wert.trim() === '') return undefined

  if (versatz === null || versatz === undefined) {
    const d = new Date(wert)
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
  }

  // Mit `Z` gelesen und dann zurückgerechnet: die Zahlen im Feld sind Ortszeit,
  // und der Weg dorthin war eine Verschiebung — hier geht sie zurück.
  const d = new Date(wert + 'Z')
  if (Number.isNaN(d.getTime())) return undefined
  return new Date(d.getTime() - versatz * MINUTE).toISOString()
}

/**
 * „UTC−4", „UTC+5:30", „UTC" — der Versatz als Text.
 *
 * Mit dem echten Minuszeichen und nicht mit dem Bindestrich: „UTC-4" liest
 * sich wie ein Bindestrich zwischen zwei Wörtern.
 */
export function versatzText(minuten: number): string {
  if (minuten === 0) return 'UTC'
  const zeichen = minuten < 0 ? '−' : '+'
  const roh = Math.abs(minuten)
  const stunden = Math.floor(roh / 60)
  const rest = roh % 60
  // Halbe und viertel Stunden gibt es wirklich: Indien +5:30, Nepal +5:45,
  // Chatham +12:45.
  return `UTC${zeichen}${stunden}${rest === 0 ? '' : ':' + String(rest).padStart(2, '0')}`
}

/** Welche Uhr gerade gilt — steht neben jeder Zeitangabe im Formular. */
export function uhrName(versatz: Versatz): string {
  const t = WORTLAUT.technik.zeit
  return versatz === null || versatz === undefined
    ? t.geraetezeit
    : t.ortszeit(versatzText(versatz))
}

/**
 * Der Versatz, auf den das Gerät gerade steht.
 *
 * `getTimezoneOffset()` zählt andersherum als alles andere hier: es sagt, wie
 * viele Minuten man von der Ortszeit abziehen muss, um auf UTC zu kommen. Für
 * Berlin im Sommer ist das −120, gemeint ist +120. Deshalb das Vorzeichen.
 */
export function versatzDesGeraets(): number {
  return -new Date().getTimezoneOffset()
}

/**
 * Die Auswahl im Formular: ein paar volle Stunden um das Gerät herum.
 *
 * Eine Liste aller Zonen wäre ein Rollfeld mit 400 Einträgen; gebraucht wird
 * das Nachstellen um ein, zwei Stunden — die Fahrt über eine Zonengrenze.
 * Wer weiter muss, findet die Zone trotzdem: von −12 bis +14 ist alles dabei.
 */
export function versatzAuswahl(): number[] {
  const raus: number[] = []
  for (let stunde = -12; stunde <= 14; stunde++) raus.push(stunde * 60)
  // Die halben und viertel Stunden dazwischen -- sonst ist Indien nicht
  // einstellbar, und dort fährt man auch hin.
  for (const halb of [-570, -270, -210, 210, 270, 330, 345, 570, 765, 825]) {
    if (!raus.includes(halb)) raus.push(halb)
  }
  return raus.sort((a, b) => a - b)
}
